// Read-only task list for JIRA-sourced workflows.
// Fetches child work items from Jira and renders them without action buttons.

import React, { useMemo } from "react";
import { Text, LoadingIndicator, Flex } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { ControlTowerContext } from "#utils/context";
import { useControlTowerContext } from "../../ControlTowerContext";
import { useJiraChildWorkItems } from "../hooks/useJiraChildWorkItems";
import { JiraTaskRow } from "./JiraTaskRow";
import * as styles from "./styles_vanilla.css";

type JiraTaskListProps = {
  /** Jira ticket key, e.g. "BT-2170" */
  parentIssueKey: string;
};

const extractNumericPart = (key: string): number => {
  const parts = key.split("-");
  const numericPart = parts[parts.length - 1];
  return parseInt(numericPart, 10) || 0;
};

export const JiraTaskList: React.FC<JiraTaskListProps> = ({ parentIssueKey }) => {
  const isCanopy = useControlTowerContext() === ControlTowerContext.CANOPY;
  const { items, loading, error } = useJiraChildWorkItems(parentIssueKey);

  const tasks = useMemo(
    () => [...items].sort((a, b) => extractNumericPart(a.key) - extractNumericPart(b.key)),
    [items],
  );

  return (
    <div className={styles.taskListContainer}>
      <div className={styles.taskListCard}>
        <div className={styles.taskListCardHeader}>
          <Text variant="h3">Tasks</Text>
        </div>

        {loading && (
          <Flex justifyContent="center" alignItems="center" style={{ padding: "24px" }}>
            <LoadingIndicator size="medium" />
          </Flex>
        )}

        {error && (
          <div className={styles.taskListCardBody}>
            <Text variant="regular-md" color={Colors.Danger600}>
              Failed to load tasks: {error.message}
            </Text>
          </div>
        )}

        {!loading && !error && !parentIssueKey && (
          <div className={styles.taskListCardBody}>
            <Text variant="regular-md" color={Colors.Neutral600}>
              No Jira ticket linked to this workflow.
            </Text>
          </div>
        )}

        {!loading && !error && parentIssueKey && tasks.length === 0 && (
          <div className={styles.taskListCardBody}>
            <Text variant="regular-md" color={Colors.Neutral600}>
              No subtasks found in Jira for {parentIssueKey}.
            </Text>
          </div>
        )}

        {!loading && !error && tasks.length > 0 && (
          <table className={styles.taskTable}>
            <thead className={styles.taskTableHead}>
              <tr>
                <th className={styles.taskTableHeaderCell}>Name</th>
                <th className={styles.taskTableHeaderCell}>Responsible party</th>
                <th className={styles.taskTableHeaderCell}>Assigned</th>
                <th className={styles.taskTableHeaderCell}>Status</th>
                {isCanopy && (
                  <th className={styles.taskTableHeaderCell} aria-label="Jira link"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {tasks.map((item, index) => (
                <JiraTaskRow key={item.id} item={item} index={index} isCanopy={isCanopy} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default JiraTaskList;
